/**
 * PasswordStrengthIndicator - Real-time password strength (0-4) with progress bar and requirement list.
 * MODULE: core/auth/frontend/components/PasswordStrengthIndicator.tsx
 */
import { useMemo } from 'react';
import { Progress } from 'flowbite-react';

const LABELS = ['Too weak', 'Weak', 'Fair', 'Good', 'Strong'];
const COLORS = ['red', 'orange', 'yellow', 'lime', 'green'] as const;

function getScore(password: string): number {
  if (!password) return 0;
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;
  return Math.min(score, 4);
}

function getChecks(password: string): { label: string; met: boolean }[] {
  return [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'One uppercase letter', met: /[A-Z]/.test(password) },
    { label: 'One lowercase letter', met: /[a-z]/.test(password) },
    { label: 'One number', met: /\d/.test(password) },
    { label: 'One special character', met: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
  ];
}

export interface PasswordStrengthIndicatorProps {
  password: string;
  onStrengthChange?: (score: number) => void;
}

export function PasswordStrengthIndicator({ password, onStrengthChange }: PasswordStrengthIndicatorProps) {
  const score = useMemo(() => getScore(password), [password]);
  const checks = useMemo(() => getChecks(password), [password]);
  const progressPct = (score / 4) * 100;

  useMemo(() => {
    onStrengthChange?.(score);
  }, [score, onStrengthChange]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600">{LABELS[score]}</span>
        <Progress progress={progressPct} color={COLORS[score]} size="sm" className="w-24" />
      </div>
      <ul className="text-sm text-gray-600 space-y-1">
        {checks.map(({ label, met }) => (
          <li key={label} className={met ? 'text-green-600' : 'text-gray-500'}>
            {met ? '✓' : '○'} {label}
          </li>
        ))}
      </ul>
    </div>
  );
}
